/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { styled, Chip, Box, TextField } from "@mui/material";

export {
  StyledBreadCrumbContainer,
  StyledLink,
  filterContainerStyles,
  selectBaseStyles,
  viewTypographyStyles,
  datePickerStyles,
} from "../classified_discount_records/styles";

const menuOrigin = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

export const dropdownMenuProps = (menuRef) => ({
  ...menuOrigin,
  PaperProps: { ref: menuRef, sx: { maxHeight: "40vh" } },
  autoFocus: false,
  MenuListProps: { sx: { pt: 0 } },
});

export const plainMenuProps = {
  ...menuOrigin,
  PaperProps: { sx: { width: "230px" } },
};

export const OwnersTableBadge = styled(Chip)({
  height: "20px",
  borderRadius: "999px",
  border: "1px solid #2b4fb9",
  backgroundColor: "transparent",
  color: "#2b4fb9",
  fontSize: "11px",
  fontWeight: 600,
  // Deliberately tighter than this file's other Chip labels (8, 6) — it's tuned so the
  // "OT" label fits inside the fixed ORDER_NO_BADGE_SLOT_WIDTH slot below without clipping.
  "& .MuiChip-label": { paddingInline: 1.5 },
});

// Must stay >= OwnersTableBadge's rendered width (label padding + text + border), or the "OT" chip clips.
export const ORDER_NO_BADGE_SLOT_WIDTH = 22;

export const orderNoCellStyles = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

export const orderNoBadgeSlotStyles = {
  width: ORDER_NO_BADGE_SLOT_WIDTH,
  flexShrink: 0,
  display: "flex",
  justifyContent: "flex-start",
};

export const CheckInCodePill = styled(Chip)({
  height: "22px",
  borderRadius: "6px",
  border: "1px solid #d0d0d5",
  backgroundColor: "#ffffff",
  color: "#515152",
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.3px",
  "& .MuiChip-label": { paddingInline: 8 },
});

export const BUCKET_PILL_COLORS = {
  managed: { bg: "#e8f5e9", color: "#2e7d32", dot: "#2e7d32" },
  unmanaged: { bg: "rgba(0,0,0,0.06)", color: "#555", dot: "#8a8a8a" },
  unclassified: { bg: "#fdecef", color: "#d32f2f", dot: "#d32f2f" },
};

export const BucketPill = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "bucketType",
})(({ bucketType }) => {
  const palette = BUCKET_PILL_COLORS[bucketType] || BUCKET_PILL_COLORS.unmanaged;

  return {
    height: "20px",
    borderRadius: "999px",
    backgroundColor: palette.bg,
    color: palette.color,
    fontSize: "12px",
    fontWeight: 600,
    cursor: "default",
    "& .MuiChip-label": {
      paddingInline: 6,
      display: "inline-flex",
      alignItems: "center",
    },
  };
});

export const bucketPillContentStyles = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
};

export const bucketDotStyles = (bucketType) => ({
  display: "inline-block",
  width: 6,
  height: 6,
  borderRadius: "50%",
  flexShrink: 0,
  backgroundColor: (BUCKET_PILL_COLORS[bucketType] || BUCKET_PILL_COLORS.unmanaged)
    .dot,
});

export const bucketCountStyles = {
  fontSize: "11px",
  fontWeight: 500,
  opacity: 0.75,
  marginTop: "3px"
};

export const bucketTooltipStyles = {
  backgroundColor: "#ffffff",
  color: "#333",
  padding: 0,
  maxWidth: 340,
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
  "& .MuiTooltip-arrow": { color: "#ffffff" },
};

export const bucketTooltipHeaderStyles = (bucketType) => {
  const palette = BUCKET_PILL_COLORS[bucketType] || BUCKET_PILL_COLORS.unmanaged;

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 3,
    padding: "8px 12px",
    borderBottom: "1px solid #eee",
    "& .bucket-tooltip-title": {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      color: palette.color,
    },
    "& .bucket-tooltip-dot": {
      display: "inline-block",
      width: 6,
      height: 6,
      borderRadius: "50%",
      flexShrink: 0,
      backgroundColor: palette.dot,
    },
    "& .bucket-tooltip-total": {
      fontSize: "12.5px",
      fontWeight: 700,
      color: "#333",
    },
  };
};

export const bucketTooltipBodyStyles = {
  padding: 0,
  maxHeight: "240px",
  overflowY: "auto",
};

export const bucketTooltipRowStyles = {
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  gap: 3,
  px: "12px",
  py: "10px",
  boxSizing: "border-box",
  fontSize: "12.5px",
  borderBottom: "1px solid #eee",
  alignItems: "center",
  "&:last-child": {
    borderBottom: "none",
  },
  "& .bucket-tooltip-item-name": { color: "#555" },
  "& .bucket-tooltip-item-amount": { fontWeight: 600, color: "#333" },
};

export const COMPLIANCE_COLORS = {
  compliant: BUCKET_PILL_COLORS.managed.color,
  nonCompliant: BUCKET_PILL_COLORS.unclassified.color,
};

export const complianceIndicatorStyles = (compliant) => ({
  backgroundColor: compliant ? COMPLIANCE_COLORS.compliant : COMPLIANCE_COLORS.nonCompliant,
  borderRadius: "50%",
  height: "20px",
  width: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const NEGATIVE_GROSS_PROFIT_COLOR = BUCKET_PILL_COLORS.unclassified.color;

export const grossProfitStyles = (negative) => ({
  color: negative ? NEGATIVE_GROSS_PROFIT_COLOR : "inherit",
  fontWeight: negative ? 600 : "inherit",
});

const flagBase = {
  height: "26px",
  borderRadius: "8px",
  fontSize: "12px",
  fontWeight: 600,
  cursor: "default",
  "& .MuiChip-label": { paddingInline: 8 },
  "& .MuiChip-icon": { fontSize: 16, marginLeft: 4 },
};

export const WarningFlagChip = styled(Chip)({
  ...flagBase,
  backgroundColor: "#fdf3d9",
  color: "#a86b00",
  "& .MuiChip-icon": { ...flagBase["& .MuiChip-icon"], color: "#e0a106" },
});

export const ValidFlagChip = styled(Chip)({
  ...flagBase,
  backgroundColor: "#e8f5e9",
  color: "#2e7d32",
  "& .MuiChip-icon": { ...flagBase["& .MuiChip-icon"], color: "#2e7d32" },
});

export const FlagsCellContainer = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  alignItems: "center",
});

export const StyledSearchBar = styled(TextField)({
  '&:hover': {
    "& .MuiOutlinedInput-notchedOutline": {
      border: '1px solid #2b4fb9 !important'
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: '1px',
    borderRadius: '25px',
  },
  '& .MuiInputBase-root': {
    borderRadius: '25px',
    height: '2.4rem',
    paddingTop: '3px',
    fontSize: '15px',
    width: '253px !important',
    backgroundColor: 'white',
    marginTop: '8px',
  },
})
